/**
 * Global distance-analysis view ("Avstandsvisning").
 *
 * Shows a vertical drop-to-ground measurement under the power lines in the
 * loaded area. This is the *global* counterpart to area-mode's per-section
 * "Vis avstander" toggle (`areaModeState.showDistances`) — both render the same
 * vertical measurement, so the two must never be enabled at the same time.
 *
 * The polygon tool calls `disableDistanceAnalysisMode` when the user starts
 * drawing so the global view yields to the polygon's own distance lines. The
 * two UI entry points for the global view (the toolbar button and the
 * "Visningsmodus" menu in MapSettings) disable themselves while area-mode is
 * active to keep the invariant.
 */

import { get } from 'svelte/store';

import { mapToolsStore } from '../map-tools-store';
import { useDataSources } from '../cesium-datasources';

/**
 * Turn the global distance view off and clear its measurement entities.
 *
 * Returns whether it was on. The polygon tool uses that to carry the user's
 * intent across into area-mode (auto-checking "Vis avstander" for a fluid
 * transition). No-op — returns `false` — when the view is already off.
 */
export function disableDistanceAnalysisMode(): boolean {
	if (get(mapToolsStore).mapStoreInputMode !== 'distanceMode') return false;

	const { distanceAnalysisTerrainProfilesDataSource, distanceAnalysisPolylinesDataSource } =
		useDataSources();

	mapToolsStore.update((store) => {
		store.mapStoreInputMode = 'default';
		return store;
	});
	distanceAnalysisTerrainProfilesDataSource.entities.removeAll();
	distanceAnalysisPolylinesDataSource.entities.removeAll();

	return true;
}
